import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';
import { Box, IconButton, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Add } from '@mui/icons-material';

import FButtonDropdown from './FButtonDropdown';
import FHButton from './FHButton';
import FHFilterChips from './FHFilterChips';
import FHAutocomplete from './FHAutocomplete';
import FSearch from './FSearch';
import FHAntdDateRangePicker from './FHAntdDateRangePicker';
import { getThemeColor } from '../../utils/common';
import { FORTA_GRAY2, FORTA_GRAY3, FORTA_BLUE, FORTA_WHITE, FORTA_GRAY4 } from '../../utils/colors';
import { GenericSearchType, Themes } from '../../constants';
import TrashIcon3 from '../svgs/TrashIcon3';

import type { FilterAndSortMode } from '../../constants';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type { IFilterChip, IOption } from '../../utils/types';

export type SearchValueType = null | string | Moment | number | [null, null] | [Moment, Moment];

export interface ISearchConfig {
  id: string;
  label: string;
  type: GenericSearchType;
  filterGroupName?: string;
  placeholder?: string;
  disableDelete?: boolean;
  defaultValue?: SearchValueType;
  options?: Array<{
    id: string | number;
    label: string;
  }>;
}

export type ISearchConfigList = Array<ISearchConfig>;

export type IGenericSearchOutput = ISearchLocalFilter[];

interface ISearchLocalFilter {
  id?: string;
  value: SearchValueType;
  label: string;
  type: GenericSearchType;
  filterGroupName?: string;
  disableDelete?: boolean;
  defaultValue?: SearchValueType;
  selectedOption?: {
    id: string | number;
    label: string;
  };
}

interface IFHGenericSearchProps {
  name: string;
  buttonLabel?: string;
  colorTheme?: Themes;
  disabled?: boolean;
  chipWrapperId?: string;
  mode?: FilterAndSortMode;
  showResetButton?: boolean;
  filtersToHydrate?: ISearchLocalFilter[];
  buttonStyles?: any;
  debounceTime?: number;
  searchConfig: ISearchConfigList;
  chipStyles?: any;
  btnDataTestId?: string;
  popupDataTestId?: string;
  enableURLFilterParams?: boolean;
  autoPersistStrategy?: 'localStorage' | 'sessionStorage';
  buttonIcon?: (props: any) => JSX.Element | null;
  onFilterChange: (data: IGenericSearchOutput) => void;
}

interface ICustomSortButtonProps {
  label: string;
  colorTheme: Themes;
  disabled: boolean;
  Icon: (props: any) => JSX.Element | null;
  buttonStyles?: any;
}

interface IFHSearchItemProps {
  helperText?: string;
  id?: string | null;
  inputToBeFocused?: string;
  value?: SearchValueType;
  index: number;
  colorTheme: Themes;
  availableOptions: Array<IOption>;
  searchConfig: ISearchConfigList;
  onFilterRemove: (index: number) => void;
  onValueChange: (data: ISearchLocalFilter, index: number) => void;
}

const DROPDOWN_BTN_ID = 'FHGenericSearchBtn';

function CustomSortButton({
  label,
  colorTheme,
  disabled,
  Icon,
  buttonStyles,
}: ICustomSortButtonProps) {
  return (
    <FHButton
      data-testid={DROPDOWN_BTN_ID}
      id={DROPDOWN_BTN_ID}
      disabled={disabled}
      sx={{
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: 'transparent',
          zIndex: 1,
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
        },
        p: 2,
        ...buttonStyles,
      }}
      colorTheme={colorTheme}
      startIcon={Icon ? <Icon sx={{ color: getThemeColor(colorTheme).primary }} /> : null}
    >
      <Typography
        color={getThemeColor(colorTheme).primary}
        fontWeight={'bold'}
        textTransform={'none'}
      >
        {label}
      </Typography>
    </FHButton>
  );
}

function FHSearchItem({
  helperText,
  id,
  index,
  value,
  colorTheme,
  inputToBeFocused,
  availableOptions = [],
  searchConfig,
  onFilterRemove,
  onValueChange,
}: IFHSearchItemProps) {
  const [selectedFilter, setSelectedFilter] = useState<IOption | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<ISearchConfig | null>(null);
  const [selectedValue, setSelectedValue] = useState<SearchValueType>(value ?? null);
  const [simulateClick, setSimulateClick] = useState<boolean>(false);
  const availableOptionsCopy = JSON.parse(JSON.stringify(availableOptions));

  const eleId =
    selectedConfig?.type === GenericSearchType.AutoComplete
      ? `fhGenericSearchAutocomplete-${index}`
      : selectedConfig?.type === GenericSearchType.DateRange
        ? `fhGenericSearchDateInput-${index}`
        : `fhFilterTextfield-${index}`;

  useEffect(() => {
    if (!simulateClick) return;
    const ele = document.getElementById(eleId);
    ele?.focus();
  }, [simulateClick, eleId]);

  useEffect(() => {
    if (!inputToBeFocused) return;
    if (inputToBeFocused === selectedFilter?.id) {
      setSimulateClick(true);
    }
  }, [inputToBeFocused, index, selectedFilter, selectedValue]);

  useEffect(() => {
    if (!id) return;
    const selected = searchConfig.find((item) => String(item.id) === String(id));
    if (selected) {
      setSelectedFilter({
        id: selected.id,
        label: selected.label,
      });
    }
  }, [searchConfig, id]);

  useEffect(() => {
    if (!selectedFilter) return;
    const selected = searchConfig.find((item) => String(item.id) === String(selectedFilter.id));
    if (selected) {
      setSelectedConfig(selected);
    }
  }, [selectedFilter, searchConfig]);

  useEffect(() => {
    if (!selectedConfig) return;
    valueChange(selectedConfig, selectedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfig, selectedValue]);

  const valueChange = (selectedConfig: ISearchConfig, value: SearchValueType) => {
    if (selectedConfig === null) return;
    const data: ISearchLocalFilter = {
      id: selectedConfig.id,
      value,
      label: selectedConfig.label,
      type: selectedConfig.type,
      defaultValue: selectedConfig.defaultValue,
      disableDelete: selectedConfig.disableDelete,
      filterGroupName: selectedConfig.filterGroupName,
    };
    // Setting selected option, to show correct labels in filter chips
    if (selectedConfig.type === GenericSearchType.AutoComplete) {
      const selected = selectedConfig.options?.find((item) => String(item.id) === String(value));
      data.selectedOption = selected;
    }
    onValueChange(data, index);
  };

  const handleRemoveSearchFilter = () => {
    onFilterRemove(index);
  };

  const handleValueChange = (val: any) => {
    setSelectedValue(val);
  };

  const handleDateRangeChange = (value: [Moment, Moment] | null | [null, null]) => {
    if (value) {
      const [startdate, endDate] = value;
      if (startdate && endDate) {
        setSelectedValue([
          startdate?.seconds(0)?.milliseconds(0),
          endDate?.seconds(0)?.milliseconds(0),
        ]);
      }
    } else {
      setSelectedValue([null, null]);
    }
  };

  const targetOptions = availableOptionsCopy;
  if (selectedConfig?.filterGroupName) {
    searchConfig
      .filter((item) => item.filterGroupName === selectedConfig.filterGroupName)
      .forEach((item) => {
        targetOptions.push({
          id: item.id,
          label: item.label,
        });
      });
  }

  return (
    <>
      {helperText && (
        <Typography fontWeight={700} color={getThemeColor(colorTheme).primary} mb={1.5}>
          {helperText}
        </Typography>
      )}
      <Box sx={{ display: 'flex', mb: 2 }} data-testid={`fhSearchItem-${index}`}>
        <FHAutocomplete
          sx={{
            width: '240px',
            mr: 1,
            borderRadius: '10px 0 0 10px',
            boxShadow: 'none',
            opacity: targetOptions.length < 1 ? 0.8 : 1,
            '& input::placeholder': {
              color: `${FORTA_GRAY3} !important`,
            },
            '.MuiIconButton-root.Mui-disabled': {
              opacity: 0.2,
            },
          }}
          data-testid={`fhSearchItem-target-${index}`}
          inputHeight={40}
          colorTheme={colorTheme}
          coloredBackground
          disableClearable
          value={selectedFilter}
          placeholder="Select search filter"
          isOptionEqualToValue={(option: any, value: any) => String(option.id) === String(value.id)}
          options={targetOptions}
          disabled={targetOptions.length < 1 || selectedConfig?.defaultValue}
          onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
            setSelectedFilter(newValue);
            setSelectedValue(null);
          }}
        />
        {(!selectedConfig || selectedConfig.type === GenericSearchType.TextInput) && (
          <FSearch
            sx={{
              flexGrow: 1,
              mr: 1,
              borderRadius: '0 10px 10px 0',
              minWidth: '290px',
              height: '40px',
              boxShadow: 'none',
              '&.Mui-disabled': {
                opacity: '0.2 !important',
                background: FORTA_GRAY2,
                cursor: 'not-allowed !important',
              },
              '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY3 },
            }}
            disabled={!selectedConfig || !id}
            inputId={eleId}
            value={String(selectedValue ?? '')}
            colorTheme={colorTheme}
            coloredBackground
            autoComplete="off"
            placeholder={selectedConfig?.placeholder ?? ''}
            showStartAdornment={true}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleValueChange(e.target.value);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          />
        )}
        {selectedConfig && selectedConfig.type === GenericSearchType.DateRange && (
          <Box sx={{ mr: 1 }}>
            <FHAntdDateRangePicker
              data-testid={eleId}
              id={eleId}
              inputHeight={40}
              coloredBackground
              format="MM/DD/YYYY"
              showNow={true}
              changeOnBlur={true}
              use12Hours
              allowClear={false}
              value={selectedValue}
              colorTheme={colorTheme}
              disabled={!id}
              onChange={handleDateRangeChange}
              onCalendarChange={handleDateRangeChange}
              sx={{
                minWidth: '290px',
                flexGrow: 1,
                borderRadius: '0 10px 10px 0',
                boxShadow: 'none',
                height: '40px',
                '&.Mui-disabled': {
                  opacity: '0.2 !important',
                  background: FORTA_GRAY2,
                  cursor: 'not-allowed !important',
                },
              }}
            />
          </Box>
        )}
        {selectedConfig && selectedConfig.type === GenericSearchType.AutoComplete && (
          <FHAutocomplete
            sx={{
              minWidth: '290px',
              mr: 1,
              flexGrow: 1,
              boxShadow: 'none',
              borderRadius: '0 10px 10px 0',
              '& input::placeholder': {
                color: `${FORTA_GRAY3} !important`,
              },
            }}
            id={eleId}
            data-testid={eleId}
            inputHeight={40}
            fullWidth
            colorTheme={colorTheme}
            coloredBackground
            placeholder="Select"
            disabled={!id}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            options={selectedConfig.options ?? []}
            value={
              selectedConfig.options?.find((item) => String(item.id) === String(selectedValue)) ??
              ''
            }
            renderOption={(props: any, option: IOption) => {
              return (
                <Box
                  {...props}
                  sx={{
                    display: 'flex !important',
                    justifyContent: 'space-between !important',
                  }}
                  key={option.id}
                >
                  <Typography>{option.label}</Typography>
                  {option.description && (
                    <Typography
                      sx={{
                        px: 0.7,
                        ml: 1,
                        borderRadius: '4px',
                        fontSize: 12,
                        fontWeight: 700,
                        color: FORTA_WHITE,
                        backgroundColor: FORTA_GRAY4,
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              );
            }}
            onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              handleValueChange(newValue?.id);
            }}
            onKeyDown={(e: ChangeEvent<HTMLSelectElement>) => {
              e.stopPropagation();
            }}
          />
        )}
        <IconButton
          sx={{
            ml: 1,
            opacity: selectedConfig?.disableDelete ? 0.2 : 1,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          disabled={selectedConfig?.disableDelete}
          data-testid={`fhGSRemoveFilterBtn-${index}`}
          onClick={handleRemoveSearchFilter}
        >
          <TrashIcon3 width={18} height={18} color={getThemeColor(colorTheme).primary} />
        </IconButton>
      </Box>
    </>
  );
}

const encodeFilterParams = (selectedFilters: any[], name: string) => {
  return Object.fromEntries([
    ['filterName', name],
    ...selectedFilters.flatMap((it) => {
      if (!it.id || !it.value) return [];
      const paramValue = Array.isArray(it.value) ? it.value.join(it.id) : it.value;
      return [[it.id, encodeURIComponent(paramValue)]];
    }),
  ]);
};

const decodeFilterParams = (searchParams: any, searchConfig: ISearchConfigList) => {
  const searchParamFilters: ISearchLocalFilter[] = [];
  searchConfig.forEach((conf) => {
    const param = searchParams.get(conf.id);
    let value: SearchValueType;
    if (param) {
      const val = param.split(conf.id);
      if (val.length > 1) {
        value = val.map((item: string) => decodeURIComponent(item));
      } else {
        value = decodeURIComponent(param);
      }
      const { options, ...confWithoutOptions } = conf;
      const confData: ISearchLocalFilter = {
        ...confWithoutOptions,
        value,
      };
      if (conf.type === GenericSearchType.AutoComplete) {
        const selected = options?.find((item) => String(item.id) === String(value));
        confData.selectedOption = selected;
      }
      searchParamFilters.push(confData);
    }
  });
  return searchParamFilters?.length > 0 ? JSON.stringify(searchParamFilters) : null;
};

function FHGenericSearch({
  name,
  buttonLabel = 'Search',
  colorTheme = Themes.Primary,
  disabled = false,
  chipWrapperId = '',
  buttonStyles,
  searchConfig,
  debounceTime = 1000,
  chipStyles = {},
  btnDataTestId,
  popupDataTestId,
  autoPersistStrategy,
  filtersToHydrate,
  enableURLFilterParams = false,
  buttonIcon = SearchIcon,
  onFilterChange,
}: IFHGenericSearchProps) {
  const chipWrapperRef = useRef<HTMLElement>();

  const [searchParams, setSearchParams] = useSearchParams();
  const [inputToBeFocused, setInputToBeFocused] = useState<string | null>(null);

  const storageMethod =
    autoPersistStrategy === 'localStorage'
      ? localStorage
      : autoPersistStrategy === 'sessionStorage'
        ? sessionStorage
        : null;

  const storageKey = `${name}-genericSearchFilters`;

  const [selectedFilters, setSelectedFilters] = useState<ISearchLocalFilter[]>([
    { value: null, label: '', type: GenericSearchType.TextInput },
  ]);
  const [previousOutput, setPreviousOutput] = useState<IGenericSearchOutput>([]);
  const [previousStorageKey, setPreviousStorageKey] = useState<string>('');
  const [closePopup, setClosePopup] = useState<boolean>(false);
  const [chipData, setChipData] = useState<IFilterChip[]>([]);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);

  const getChipData = useCallback((selectedFilters: ISearchLocalFilter[]) => {
    setChipData(
      selectedFilters
        .filter((item) => Boolean(item.id) && Boolean(item.value))
        .map((item) => {
          let label = `${item.label} - ${item.value}`;
          if (item.type === GenericSearchType.AutoComplete) {
            label = `${item.label} - ${
              item.selectedOption ? item.selectedOption.label : item.value
            }`;
          }
          if (
            item.type === GenericSearchType.DateRange &&
            item.value &&
            Array.isArray(item.value) &&
            item.value[0] &&
            item.value[1]
          ) {
            label = `${item.label} - ${moment(item.value[0]).format('MM/DD/YYYY')} to ${moment(
              item.value[1],
            ).format('MM/DD/YYYY')}`;
          }
          const data = {
            id: item.id ?? '',
            label,
            filterName: item.id ?? '',
            disableDelete: Boolean(item.disableDelete),
          };
          return data;
        }) || [],
    );
  }, []);

  // Automatically persist filters in storage
  useEffect(() => {
    setPreviousStorageKey(storageKey);
    setPreviousOutput([]);

    const defaultFilterConfigs = searchConfig?.filter((conf) => conf.defaultValue);
    const defaultFilters = defaultFilterConfigs.map((conf) => ({
      id: conf.id,
      value: conf.defaultValue ?? null,
      label: conf.label,
      type: conf.type,
      defaultValue: conf.defaultValue,
      disableDelete: conf.disableDelete,
      filterGroupName: conf.filterGroupName,
    }));

    let persistedFilters = null;

    // Check if filters are present as search params
    if (enableURLFilterParams) {
      const searchParamFilters = decodeFilterParams(searchParams, searchConfig);
      if (searchParamFilters) {
        persistedFilters = searchParamFilters;
      }
    }

    // Check for persisted filters
    if (autoPersistStrategy && storageMethod && storageKey) {
      if (!persistedFilters) {
        persistedFilters = storageMethod.getItem(storageKey);
      }
      if (!persistedFilters) {
        if (defaultFilters.length) {
          setSelectedFilters(defaultFilters);
        } else {
          setSelectedFilters([{ value: null, label: '', type: GenericSearchType.TextInput }]);
        }
        return;
      }
    }

    // If previous filters persists, process them in correct format
    const processedPersistedFilters: IGenericSearchOutput = persistedFilters
      ? JSON.parse(persistedFilters)?.map((item: ISearchLocalFilter) => {
          if (
            Array.isArray(item?.value) &&
            item.value.length === 2 &&
            item.value[0] &&
            item.value[1] &&
            item.type === GenericSearchType.DateRange
          ) {
            return {
              ...item,
              value: [moment(item.value[0]), moment(item.value[1])],
            };
          }
          return item;
        })
      : [];

    const processedFilterIds = processedPersistedFilters.map((conf) => conf.id);

    const combinedFilters = [
      ...processedPersistedFilters,
      ...defaultFilters.filter((conf) => !processedFilterIds.includes(conf.id)),
    ];
    setSelectedFilters(
      combinedFilters.length
        ? combinedFilters
        : [{ value: null, label: '', type: GenericSearchType.TextInput }],
    );
  }, [searchConfig, autoPersistStrategy, enableURLFilterParams]);

  // Preselect filters on load
  useEffect(() => {
    if (
      !filtersToHydrate ||
      !filtersToHydrate?.length ||
      selectedFilters?.length > 1 ||
      autoPersistStrategy ||
      (selectedFilters?.length === 1 && selectedFilters[0].id)
    ) {
      return;
    }
    const processedFilters: IGenericSearchOutput = filtersToHydrate.map((item) => {
      if (
        Array.isArray(item?.value) &&
        item.value.length === 2 &&
        item.value[0] &&
        item.value[1] &&
        item.type === GenericSearchType.DateRange
      ) {
        return {
          ...item,
          value: [moment(item.value[0]), moment(item.value[1])],
        };
      }
      return item;
    });
    setSelectedFilters(processedFilters);
  }, [filtersToHydrate]);

  const handleTogglePopup = (state = false) => {
    setClosePopup(state);
    setInputToBeFocused(null);
  };

  useEffect(() => {
    if (!chipWrapperId) return;
    const timeoutId = setTimeout(() => {
      chipWrapperRef.current = document.getElementById(chipWrapperId) ?? undefined;
    }, 0);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [chipWrapperId]);

  useEffect(() => {
    if (!closePopup) return;
    handleTogglePopup();
  }, [closePopup]);

  useEffect(() => {
    const filtered = selectedFilters.filter((item) => Boolean(item.id));
    if (selectedFilters.length === filtered.length) {
      setIsAdding(false);
    }
    setPreviousOutput(filtered);
    if (
      _.isEqual(filtered, previousOutput) ||
      (previousStorageKey && storageKey !== previousStorageKey)
    ) {
      if (!filtered.length) {
        debouncedOnFilterChange([]);
        debouncedGetChipData([]);
      }
      return;
    }
    debouncedOnFilterChange(filtered);
    debouncedGetChipData(selectedFilters);
    if ((autoPersistStrategy && storageMethod) || enableURLFilterParams) {
      const serializableFilters = selectedFilters?.map((item) => {
        if (
          Array.isArray(item.value) &&
          item.value[0] &&
          item.value[1] &&
          item.type === GenericSearchType.DateRange
        ) {
          return {
            ...item,
            value: [
              moment(item.value[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
              moment(item.value[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            ],
          };
        }
        return item;
      });
      if (storageMethod) {
        storageMethod.setItem(storageKey, JSON.stringify(serializableFilters));
      }
      if (enableURLFilterParams) {
        const urlParamFilters = encodeFilterParams(serializableFilters, name);
        setSearchParams(urlParamFilters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnFilterChange = useCallback(_.debounce(onFilterChange, debounceTime), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetChipData = useCallback(_.debounce(getChipData, debounceTime), []);

  const handleAddNewFilter = () => {
    if (selectedFilters.length >= searchConfig.length) return;
    setSelectedFilters((prev) => [
      ...prev,
      { value: null, label: '', type: GenericSearchType.TextInput },
    ]);
  };

  const handleSingleItemRemove = () => {
    if (selectedFilters.length > 1) return;
    setSelectedFilters([
      { id: undefined, value: null, label: '', type: GenericSearchType.TextInput },
    ]);
    handleTogglePopup(true);
  };

  const handleChipRemove = (data: IFilterChip[]) => {
    if (disabled) return;
    if (selectedFilters.length === 1) {
      handleSingleItemRemove();
      return;
    }
    const selectedIds = data.map((item) => item.id);
    const filtered = selectedFilters.filter(
      (item) => selectedIds.includes(item.id ?? '') || !item.id || !item.value,
    );
    if (filtered.length === 0) {
      handleSingleItemRemove();
      return;
    }
    setSelectedFilters(filtered);
  };

  const handleChipClick = (id: number | string) => {
    if (disabled) return;
    setInputToBeFocused(String(id));
    setOpenFilter(true);
  };

  const selectedIds = selectedFilters.map((item) => item.id);
  const selectedGroups: { [key: string]: boolean } = {};
  const allGroups: { [key: string]: boolean } = {};

  selectedFilters.forEach((item) => {
    if (item.filterGroupName && !(item.filterGroupName in selectedGroups)) {
      selectedGroups[item.filterGroupName] = true;
    }
  });
  searchConfig.forEach((item) => {
    if (item.filterGroupName && !(item.filterGroupName in allGroups)) {
      allGroups[item.filterGroupName] = true;
    }
  });
  const uniqueFilters =
    searchConfig.filter((item) => !item.filterGroupName).length + Object.keys(allGroups).length;

  const availableOptions = searchConfig
    .filter(
      (item) =>
        !selectedIds.includes(item.id) &&
        !(item.filterGroupName && item.filterGroupName in selectedGroups),
    )
    .map((item) => ({
      id: item.id,
      label: item.label,
    }));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <FButtonDropdown
        btnId={DROPDOWN_BTN_ID}
        dataTestId={btnDataTestId}
        popupDataTestId={popupDataTestId}
        triggerOpen={openFilter}
        close={closePopup}
        onClose={() => {
          setOpenFilter(false);
        }}
        buttonRenderer={() => (
          <CustomSortButton
            label={buttonLabel}
            colorTheme={colorTheme}
            disabled={disabled}
            Icon={buttonIcon}
            buttonStyles={buttonStyles}
          />
        )}
        disabled={disabled}
        preventCloseOnBodyClick
        colorTheme={colorTheme}
        sx={{
          '& .MuiPaper-elevation': {
            border: `solid 2px ${FORTA_BLUE.light3}`,
            borderRadius: '10px',
            overflowY: 'auto',
          },
          padding: '20px',
          zIndex: 1000,
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography fontWeight={700} mb={1}>
              Search by
            </Typography>
            <IconButton
              disableRipple
              data-testid="fhGenericSearchMinimiseIcon"
              sx={{
                color: getThemeColor(colorTheme).primary,
                mt: -5,
                mr: 0.5,
                border: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              onClick={() => handleTogglePopup(true)}
            >
              _
            </IconButton>
          </Box>
          <Box>
            {selectedFilters.map((item, index) => {
              return (
                <FHSearchItem
                  key={item.id ?? index}
                  id={item.id}
                  index={index}
                  inputToBeFocused={inputToBeFocused ?? ''}
                  helperText={index > 0 ? 'And' : ''}
                  value={item.value}
                  colorTheme={colorTheme}
                  availableOptions={availableOptions}
                  searchConfig={searchConfig}
                  onFilterRemove={(i) => {
                    if (selectedFilters.length === 1) {
                      handleSingleItemRemove();
                      return;
                    }
                    setSelectedFilters((prev) => {
                      return prev.filter((_, index) => index !== i);
                    });
                  }}
                  onValueChange={(v, i) => {
                    setSelectedFilters((prev) => {
                      return prev.map((item, index) => {
                        if (index === i) {
                          return v;
                        }
                        return item;
                      });
                    });
                  }}
                />
              );
            })}
          </Box>
          {selectedFilters.length < uniqueFilters && (
            <FHButton
              startIcon={
                <Add
                  sx={{
                    color: isAdding
                      ? getThemeColor(colorTheme).light3
                      : getThemeColor(colorTheme).primary,
                  }}
                />
              }
              disableRipple
              disabled={isAdding}
              data-testid="fhGenericSearchAddBtn"
              sx={{
                mt: -2,
                mb: -2.4,
                backgroundColor: 'transparent',
                color: getThemeColor(colorTheme).primary,
                boxShadow: 'none',
                border: 'none',
                '&.Mui-disabled': {
                  backgroundColor: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                  color: getThemeColor(colorTheme).light3,
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: getThemeColor(colorTheme).primary,
                  boxShadow: 'none',
                },
              }}
              onClick={() => {
                handleAddNewFilter();
                setIsAdding(true);
              }}
            >
              Add
            </FHButton>
          )}
        </Box>
      </FButtonDropdown>
      {chipWrapperRef.current &&
        createPortal(
          <FHFilterChips
            chipData={chipData}
            onChipRemove={handleChipRemove}
            onClick={handleChipClick}
            chipStyles={chipStyles}
            colorTheme={colorTheme}
          />,
          chipWrapperRef.current,
        )}
    </Box>
  );
}

export default FHGenericSearch;
